import { useSearchParams } from "react-router-dom"
import { useCallback, useMemo } from "react"

export type ParamType = "string" | "number" | "json" | "boolean"

type ParamTypeMap = {
  string: string
  number: number
  json: object
  boolean: boolean
}

type Filters<T extends Record<string, ParamType>> = {
  [K in keyof T]: ParamTypeMap[T[K]] // | null | undefined
}

export function useFilters<
  T extends Record<string, ParamType>,
  D extends Partial<{ [K in keyof T]: ParamTypeMap[T[K]] }>,
>({ params = {} as T, defaults = {} as D }: { params?: T; defaults?: D }) {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo(
    () =>
      Object.keys(params).reduce((acc, key) => {
        const type = params[key as keyof T]
        const raw = searchParams.get(key)

        let value: unknown = raw ?? defaults[key as keyof T]

        if (raw !== null) {
          if (type === "number") {
            value = Number(raw)
          }
          if (type === "json") {
            value = JSON.parse(raw)
          }
          if (type === "boolean") {
            value = raw === "true" ? true : raw === "false" ? false : undefined
          }
        }

        return { ...acc, [key]: value }
      }, {} as Filters<T>),
    [defaults, params, searchParams],
  )

  const serializableFilters = useMemo(
    () =>
      Object.keys(params).reduce((acc, key) => {
        const type = params[key as keyof T]
        const raw = searchParams.get(key)

        let value: unknown = raw ?? defaults[key as keyof T]

        if (raw !== null) {
          if (type === "number") {
            value = Number(raw)
          }
          if (type === "boolean") {
            value = raw === "true" ? true : raw === "false" ? false : undefined
          }
        }

        return { ...acc, [key]: value }
      }, {} as Filters<T>),
    [defaults, params, searchParams],
  )

  const updateFilters = useCallback(
    (newFilters: Partial<Record<keyof T, string | null>>) => {
      const merged = { ...filters, ...newFilters }

      const urlParams = new URLSearchParams()
      Object.entries(merged).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") {
          urlParams.set(k, params[k] === "json" ? JSON.stringify(v) : String(v))
        }
      })

      setSearchParams(urlParams, { replace: true })
    },
    [filters, params, setSearchParams],
  )

  return { filters, updateFilters, serializableFilters }
}
